import { useEffect, useState } from 'react';
import { formatBytes } from '../lib/emitter.ts';
import { canSaveToFolder, downloadJournal, downloadLocalFile, exportName, listLocalTakes, saveTakeToFolder, type LocalTake } from '../lib/local-copies.ts';
import { localFiles } from '../lib/storage.ts';
import { Icon, useInterval } from './common.tsx';

/**
 * "Recordings on this computer": the original files in this browser's storage, which anyone can save out
 * at any time, with or without the server.
 */
export function LocalCopies({ studioId, compact }: { studioId?: string; compact?: boolean }) {
  const storage = localFiles();
  const [takes, setTakes] = useState<LocalTake[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () =>
    void listLocalTakes(storage)
      .then((t) => setTakes(studioId ? t.filter((x) => x.journal.studioId === studioId) : t))
      .catch(() => setTakes([]));
  useEffect(refresh, [studioId]);
  useInterval(refresh, 10_000);
  if (!takes?.length) return null;

  const run = async (id: string, fn: () => Promise<string | void>) => {
    setBusy(id);
    setNote(null);
    try {
      const where = await fn();
      if (where) setNote({ ok: true, text: `Saved to “${where}”.` });
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setNote({ ok: false, text: `Couldn’t save: ${(e as Error).message}` });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={compact ? 'col' : 'card col'} style={{ gap: 10 }}>
      <div className="col" style={{ gap: 2 }}>
        <h3>Recordings on this computer</h3>
        <p className="panel-note">
          The original files, kept in this browser’s storage until you clear them. Save a copy whenever you like: it doesn’t
          need the server.
        </p>
      </div>
      {note && (
        <div className={`alert small ${note.ok ? 'info' : 'bad'}`}>
          <Icon name={note.ok ? 'check' : 'warn'} size={13} /> {note.text}
        </div>
      )}
      {takes.slice(0, 10).map((t) => {
        const total = t.files.reduce((a, f) => a + f.size, 0);
        const live = t.files.some((f) => f.recording);
        return (
          <details key={t.journalName} className="local-take">
            <summary>
              <span className="grow ellipsis">
                <strong>Take {t.journal.takeNumber}</strong>
                {!studioId && <span className="muted"> · {t.journal.studioName}</span>}
                <span className="faint">
                  {' '}
                  · {new Date(t.journal.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {formatBytes(total)}
                </span>
              </span>
              {live && (
                <span className="pill rec">
                  <span className="dot pulse" /> REC
                </span>
              )}
            </summary>
            <div className="col" style={{ gap: 6, marginTop: 8 }}>
              {t.files.map((f) => (
                <div key={f.entry.localName} className="row between small">
                  <span className="ellipsis">
                    {f.entry.label} <span className="faint">· {f.entry.ext.toUpperCase()} · {formatBytes(f.size)}</span>
                  </span>
                  <button
                    className="ghost sm icon"
                    disabled={f.recording || busy != null}
                    title={f.recording ? 'Still recording' : 'Download this file'}
                    aria-label={`Download ${f.entry.label}`}
                    onClick={() => run(f.entry.localName, () => downloadLocalFile(storage, f.entry.localName, exportName(t, f.entry)))}
                  >
                    <Icon name="download" size={14} />
                  </button>
                </div>
              ))}
              <div className="row wrap" style={{ gap: 6, marginTop: 2 }}>
                {canSaveToFolder() && (
                  <button className="sm" disabled={busy != null || t.files.every((f) => f.recording)} onClick={() => run(t.journalName, () => saveTakeToFolder(storage, t))}>
                    {busy === t.journalName ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} /> : <Icon name="folder" size={14} />} Save take to a folder…
                  </button>
                )}
                <button className="ghost sm" onClick={() => downloadJournal(t)} title="This computer's record of the take: files, sync marks, events">
                  Journal (.json)
                </button>
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );
}
